import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AppFooter } from "@/components/AppFooter";

const empty = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll get back to you shortly");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="contact-page">
      <main className="flex-1 px-6 py-12">
        <div className="max-w-2xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-home">
            <ArrowLeft size={16} /> Back home
          </Link>
          <header className="mt-6">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Get in touch</p>
            <h1 className="text-4xl sm:text-5xl font-bold mt-2">Contact us</h1>
            <p className="text-muted-foreground mt-3">
              Questions, feedback, or want to work with Dt. Dana? Send a message below — we'll reply to your email within one business day.
            </p>
          </header>

          <a
            href="mailto:info@mynutritrack.app"
            className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            data-testid="contact-email-link"
          >
            <Mail size={14} /> info@mynutritrack.app
          </a>

          {sent ? (
            <div className="nt-card mt-8 p-8 text-center space-y-3" data-testid="contact-sent">
              <CheckCircle2 size={48} className="mx-auto text-primary" />
              <h2 className="text-xl font-semibold">Thanks — your message is on its way</h2>
              <p className="text-sm text-muted-foreground">
                We'll reply to <b>{form.email || "your inbox"}</b> as soon as possible.
              </p>
              <Button variant="outline" onClick={() => setSent(false)} data-testid="contact-send-another">
                Send another message
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="nt-card mt-8 p-6 space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Your name</Label>
                  <Input
                    id="contact-name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">Email</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    required
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-subject">Subject (optional)</Label>
                <Input
                  id="contact-subject"
                  value={form.subject}
                  onChange={(e) => set("subject", e.target.value)}
                  placeholder="e.g. Becoming a patient"
                  data-testid="contact-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-message">Message</Label>
                <Textarea
                  id="contact-message"
                  rows={6}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  required
                  data-testid="contact-message-input"
                />
              </div>
              <Button type="submit" disabled={sending} className="gap-2 rounded-full" data-testid="contact-submit-button">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sending ? "Sending…" : "Send message"}
              </Button>
              <p className="text-xs text-muted-foreground">
                By submitting this form you agree to our{" "}
                <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
              </p>
            </form>
          )}
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
